// UserInfoCard.vue
<template>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center py-2 px-4">
      <div class="d-flex align-center">
        <v-avatar size="40" class="mr-3">
          <v-img :src="user.avatarUrl || '/default-avatar.png'"></v-img>
        </v-avatar>
        <div>
          <div class="text-h6">{{ user.name }}</div>
          <div class="subtitle-2 grey--text text--darken-1">@{{ user.username }}</div>
        </div>
      </div>
      <div>
        <v-btn icon small @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pa-4">
      <!-- 기본 정보 -->
      <v-row>
        <v-col cols="12" md="6">
          <v-list dense>
            <v-list-item>
              <v-list-item-icon class="mr-3 my-0">
                <v-icon>mdi-badge-account</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle class="caption">GitLab ID</v-list-item-subtitle>
                <v-list-item-title>{{ user.gitlabId }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item>
              <v-list-item-icon class="mr-3 my-0">
                <v-icon>mdi-email</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle class="caption">Email</v-list-item-subtitle>
                <v-list-item-title>{{ user.email }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item v-if="user.organization">
              <v-list-item-icon class="mr-3 my-0">
                <v-icon>mdi-domain</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle class="caption">Organization</v-list-item-subtitle>
                <v-list-item-title>{{ user.organization || '-' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item v-if="user.location">
              <v-list-item-icon class="mr-3 my-0">
                <v-icon>mdi-map-marker</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle class="caption">Location</v-list-item-subtitle>
                <v-list-item-title>{{ user.location || '-' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-col>

        <v-col cols="12" md="6">
          <v-list dense>
            <v-list-item>
              <v-list-item-icon class="mr-3 my-0">
                <v-icon>mdi-clock-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle class="caption">Created At</v-list-item-subtitle>
                <v-list-item-title>{{ formatDate(user.createdAt) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item>
              <v-list-item-icon class="mr-3 my-0">
                <v-icon>mdi-calendar</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle class="caption">Last Login</v-list-item-subtitle>
                <v-list-item-title>{{ formatDate(user.lastLoginAt) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item>
              <v-list-item-icon class="mr-3 my-0">
                <v-icon>mdi-account-clock</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle class="caption">Last Activity</v-list-item-subtitle>
                <v-list-item-title>{{ formatDate(user.lastActivityOn) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>

      <!-- 통계 -->
      <v-row class="mt-2">
        <v-col cols="12" class="py-2">
          <div class="subtitle-1 font-weight-bold mb-3">Statistics</div>
          <v-row>
            <v-col cols="6" sm="3">
              <v-card outlined class="text-center pa-3">
                <div class="text-h6 primary--text">{{ user.totalMergeRequests || 0 }}</div>
                <div class="caption grey--text">Total MRs</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card outlined class="text-center pa-3">
                <div class="text-h6 warning--text">{{ user.openMergeRequests || 0 }}</div>
                <div class="caption grey--text">Open MRs</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card outlined class="text-center pa-3">
                <div class="text-h6 info--text">{{ user.totalIssues || 0 }}</div>
                <div class="caption grey--text">Total Issues</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card outlined class="text-center pa-3">
                <div class="text-h6 error--text">{{ user.openIssues || 0 }}</div>
                <div class="caption grey--text">Open Issues</div>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <!-- 상태 및 권한 -->
      <v-row class="mt-2">
        <v-col cols="12" class="py-2">
          <div class="subtitle-1 font-weight-bold mb-3">Status & Roles</div>
          <div class="d-flex flex-wrap">
            <v-chip
                small
                class="mr-2 mb-2"
                :color="user.state === 'active' ? 'success' : 'error'"
                dark
            >
              {{ user.state }}
            </v-chip>
            <v-chip
                v-if="user.linked"
                small
                class="mr-2 mb-2"
                color="primary"
                dark
            >
              SCM Linked
            </v-chip>
            <v-chip
                v-if="user.admin"
                small
                class="mr-2 mb-2"
                color="amber darken-2"
                dark
            >
              GitLab Admin
            </v-chip>
            <v-chip
                v-for="role in user.roles"
                :key="role"
                small
                class="mr-2 mb-2"
                :color="getRoleColor(role)"
                dark
            >
              {{ role }}
            </v-chip>
          </div>
        </v-col>
      </v-row>

      <!-- SCM 롤 승인/변경 (admin 전용, SCM 연동 유저) -->
      <v-row v-if="isAdmin && user.linked" class="mt-2">
        <v-col cols="12" class="py-2">
          <div class="subtitle-1 font-weight-bold mb-2">Set SCM Role</div>
          <div class="d-flex align-center flex-wrap" style="gap: 12px;">
            <v-select
                v-model="selectedRole"
                :items="roleOptions"
                dense outlined hide-details
                label="Role"
                style="max-width: 280px"
            ></v-select>
            <v-btn color="primary" small :disabled="selectedRole === currentRole" @click="applyRole">
              <v-icon left small>mdi-content-save</v-icon> Apply
            </v-btn>
          </div>
          <div class="caption grey--text mt-2">
            guest = view only · user = deploy assigned stacks · admin = full access
          </div>
        </v-col>
      </v-row>

      <!-- 배포 허용 스택 (admin 이 유저별 지정, user 롤에 적용) -->
      <v-row v-if="isAdmin && user.linked" class="mt-0">
        <v-col cols="12" class="py-2">
          <div class="subtitle-1 font-weight-bold mb-2">Deploy Stacks</div>
          <div class="d-flex align-center flex-wrap" style="gap: 12px;">
            <v-select
                v-model="selectedStacks"
                :items="availableStacks"
                dense outlined hide-details multiple chips small-chips
                label="Allowed stacks"
                style="max-width: 420px"
            ></v-select>
            <v-btn color="primary" small :disabled="!stacksChanged" @click="applyScopes">
              <v-icon left small>mdi-content-save</v-icon> Apply
            </v-btn>
          </div>
          <div class="caption grey--text mt-2">
            user 롤에 적용 — 지정된 스택만 배포 가능. 비어있으면 배포 불가. admin 은 전체.
          </div>
        </v-col>
      </v-row>
    </v-card-text>

    <v-divider></v-divider>

    <v-card-actions class="pa-4">
      <v-spacer></v-spacer>
      <v-btn
          small
          outlined
          :color="user.enabled ? 'error' : 'success'"
          @click="$emit('toggle-status', user)"
          v-if="user.linked"
      >
        <v-icon left small>
          {{ user.enabled ? 'mdi-account-off' : 'mdi-account-check' }}
        </v-icon>
        {{ user.enabled ? 'Disable' : 'Enable' }}
      </v-btn>
      <v-btn
          small
          color="primary"
          class="ml-2"
          :href="user.webUrl"
          target="_blank"
          v-if="user.webUrl"
      >
        <v-icon left small>mdi-open-in-new</v-icon>
        GitLab Profile
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  props: {
    user: {
      type: Object,
      required: true
    },
    availableStacks: {
      type: Array,
      default: () => []
    }
  },

  data: () => ({
    selectedRole: 'GUEST',
    selectedStacks: []
  }),

  computed: {
    isAdmin() { return this.$store.getters.isAdmin },
    roleOptions() {
      return [
        { text: 'Guest — view only', value: 'GUEST' },
        { text: 'User — deploy assigned stacks', value: 'USER' },
        { text: 'Admin — full access', value: 'ADMIN' }
      ]
    },
    currentRole() {
      const r = this.user?.roles || []
      if (r.includes('ADMIN')) return 'ADMIN'
      if (r.includes('USER')) return 'USER'
      return 'GUEST'
    },
    currentStacks() { return [...(this.user?.scopes || [])] },
    stacksChanged() {
      const a = [...(this.selectedStacks || [])].sort()
      const b = [...this.currentStacks].sort()
      return a.length !== b.length || a.some((v, i) => v !== b[i])
    }
  },

  watch: {
    user: { immediate: true, handler() { this.selectedRole = this.currentRole; this.selectedStacks = [...this.currentStacks] } }
  },

  methods: {
    // admin 선택 시 USER 도 함께 부여 — 로그인 동기화가 비-GitLab-admin 의 ADMIN 을 떼도 최소 user 유지
    applyRole() {
      const roles = this.selectedRole === 'ADMIN' ? ['USER', 'ADMIN'] : [this.selectedRole]
      this.$emit('update-role', { user: this.user, roles })
    },

    applyScopes() {
      this.$emit('update-scopes', { user: this.user, scopes: [...this.selectedStacks] })
    },

    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).replace(/\//g, '.').replace(',', '')
    },

    getRoleColor(role) {
      const colors = {
        ADMIN: 'deep-purple',
        USER: 'blue',
        GUEST: 'blue-grey'
      };
      return colors[role] || 'grey'
    }
  }
}
</script>

<style scoped>
.v-list-item {
  min-height: 40px !important;
}

.v-card.stat-card {
  transition: transform 0.2s;
}

.v-card.stat-card:hover {
  transform: translateY(-2px);
}
</style>